'use client';

/** @jsxImportSource react */

import { useEffect } from 'react';

import { usePathname } from 'next/navigation';
import Script from 'next/script';

import { META_PIXEL_HOST_GUARD } from '@OnArk/utils/metaPixel';

/** Not secrets: both ship to the browser in AdRoll's own install snippet. */
export const ADROLL_ADV_ID = 'UPO7THP35ZHBNDMSZZW6I3';
const ADROLL_PIX_ID = '5ZISCUKZKZGDJKC6OJ66XH';

/**
 * AdRoll's install snippet, minus its trailing `adroll.track("pageView")`.
 *
 * Wrapped in `META_PIXEL_HOST_GUARD`, which despite the name is the shared
 * "production onark.app host AND no Global Privacy Control" gate: previews and
 * local dev stay out of the retargeting audiences, and a GPC visitor never
 * loads the tag at all (the /cookies page promises that for every ad tag).
 *
 * EU/UK consent is AdRoll's own banner (chosen in AdRoll > Settings > GDPR),
 * which roundtrip.js shows before it collects anything, so nothing here asks.
 */
export const ADROLL_SNIPPET = `
if (${META_PIXEL_HOST_GUARD}) {
adroll_adv_id = "${ADROLL_ADV_ID}";
adroll_pix_id = "${ADROLL_PIX_ID}";
adroll_version = "2.0";
(function(w, d, e, o, a) {
w.adroll_tag_source = w.adroll_tag_source || "manual";
w.__adroll_loaded = true;
w.adroll = w.adroll || [];
w.adroll.f = [ 'setProperties', 'identify', 'track', 'identify_email', 'get_cookie' ];
var roundtripUrl = "https://s.adroll.com/j/" + adroll_adv_id + "/roundtrip.js";
for (a = 0; a < w.adroll.f.length; a++) {
w.adroll[w.adroll.f[a]] = w.adroll[w.adroll.f[a]] || (function(n) {
return function() { w.adroll.push([ n, arguments ]) }
})(w.adroll.f[a])
}
e = d.createElement('script');
o = d.getElementsByTagName('script')[0];
e.async = 1;
e.src = roundtripUrl;
o.parentNode.insertBefore(e, o);
})(window, document);
}`;

/**
 * AdRoll retargeting pixel for the marketing site, `/start/*` and `/sign-up`:
 * the same three surfaces the Meta pixel covers, and never church guest pages
 * or the logged-in app.
 *
 * pageView fires from an effect per pathname, not from the snippet, for the
 * reasons spelled out in `MetaPixel.tsx`: soft navs count, and a remount (where
 * `next/script` won't re-run a cached snippet) still counts. Off-host or under
 * GPC `window.adroll` never exists, so the effect no-ops.
 */
export function AdRollPixel() {
  const pathname = usePathname();

  useEffect(() => {
    if (typeof window.adroll?.track === 'function') {
      window.adroll.track('pageView');
    }
  }, [pathname]);

  return (
    <Script
      dangerouslySetInnerHTML={{ __html: ADROLL_SNIPPET }}
      id="adroll-pixel"
      strategy="afterInteractive"
    />
  );
}
